import { useParams, useSearchParams, Link } from 'react-router-dom'
import { ArrowLeft, Download, AlertTriangle, AlertCircle, Info, ExternalLink } from 'lucide-react'
import { useApi } from '../../hooks/useApi'

interface IssueDetailData {
  success: boolean
  issueType: string
  severity: 'critical' | 'warning' | 'info'
  section: string
  title: string
  explanation: string
  impact: string
  totalAffected: number
  pages: {
    pageId: number
    url: string
    statusCode: number | null
    isIndexable: boolean
    isAsset: boolean
    currentValue: string
    suggestion: string
    title: string | null
    h1: string | null
    wordCount: number | null
  }[]
}

const SEVERITY_STYLES = {
  critical: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-100 text-red-700', icon: AlertTriangle },
  warning: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  info: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-700', icon: Info },
}

export default function AuditIssueDetail() {
  const { projectId, issueType } = useParams<{ projectId: string; issueType: string }>()
  const [searchParams] = useSearchParams()
  const auditId = searchParams.get('auditId')

  const { data, loading, error } = useApi<IssueDetailData>(
    auditId && issueType ? `/api/audits/${auditId}/issues/${issueType}` : ''
  )

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white border border-[#E2E4ED] rounded-xl p-6 animate-pulse">
            <div className="h-5 bg-[#F4F5FA] rounded w-1/3 mb-3" />
            <div className="h-4 bg-[#F4F5FA] rounded w-2/3" />
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-sm text-red-500 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
          Erreur : {error}
        </div>
      </div>
    )
  }

  if (!data || data.totalAffected === 0) {
    return (
      <div className="p-6">
        <Link to={`/p/${projectId}/audit/errors`} className="inline-flex items-center gap-1.5 text-sm text-[#8B8FA7] hover:text-[#1B2559] mb-6">
          <ArrowLeft size={16} /> Retour au rapport
        </Link>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-[#1B2559] font-semibold mb-1">Aucun probl&egrave;me de ce type</p>
          <p className="text-[#8B8FA7] text-sm">Ce type d'issue n'a pas &eacute;t&eacute; d&eacute;tect&eacute; dans cet audit.</p>
        </div>
      </div>
    )
  }

  const sev = SEVERITY_STYLES[data.severity] ?? SEVERITY_STYLES.info
  const SevIcon = sev.icon

  const handleCsvExport = () => {
    window.open(`/api/audits/${auditId}/issues/${issueType}?format=csv`, '_blank')
  }

  return (
    <div className="p-6 space-y-6">
      <Link to={`/p/${projectId}/audit/errors`} className="inline-flex items-center gap-1.5 text-sm text-[#8B8FA7] hover:text-[#1B2559]">
        <ArrowLeft size={16} /> Retour au rapport d'erreurs
      </Link>

      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl ${sev.bg} ${sev.border} border flex items-center justify-center`}>
            <SevIcon size={20} className={sev.text} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-[#1B2559]">{data.title}</h1>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${sev.badge}`}>
                {data.severity}
              </span>
            </div>
            <p className="text-sm text-[#8B8FA7] mt-0.5">
              {data.totalAffected} page{data.totalAffected > 1 ? 's' : ''} concern&eacute;e{data.totalAffected > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button
          onClick={handleCsvExport}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#555A73] border border-[#E2E4ED] rounded-lg hover:bg-[#F4F5FA] transition-colors"
        >
          <Download size={14} /> Exporter CSV
        </button>
      </div>

      <div className={`${sev.bg} ${sev.border} border rounded-xl px-5 py-4`}>
        <p className={`text-sm font-medium ${sev.text} mb-1`}>Pourquoi c'est un probl&egrave;me</p>
        <p className="text-sm text-[#555A73]">{data.explanation}</p>
        <p className={`text-sm font-medium ${sev.text} mt-3 mb-1`}>Impact SEO</p>
        <p className="text-sm text-[#555A73]">{data.impact}</p>
      </div>

      <div className="bg-white border border-[#E2E4ED] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F4F5FA] text-[#8B8FA7] text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-3 font-medium">URL</th>
                <th className="text-center px-4 py-3 font-medium w-20">Statut</th>
                <th className="text-center px-4 py-3 font-medium w-20">Index.</th>
                <th className="text-left px-4 py-3 font-medium">Valeur actuelle</th>
                <th className="text-left px-4 py-3 font-medium">Correction sugg&eacute;r&eacute;e</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F4F5FA]">
              {data.pages.map(page => {
                let pathname = page.url
                try { pathname = new URL(page.url).pathname } catch {}
                return (
                  <tr key={page.pageId} className="hover:bg-[#FAFBFD] transition-colors">
                    <td className="px-4 py-3 max-w-sm">
                      <a href={page.url} target="_blank" rel="noopener noreferrer"
                        className="text-[#4318FF] hover:underline flex items-center gap-1 font-mono text-xs" title={page.url}>
                        <span className="truncate">{pathname}</span>
                        <ExternalLink size={12} className="shrink-0" />
                      </a>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-mono font-bold text-xs ${
                        page.statusCode && page.statusCode >= 400 ? 'text-red-500'
                          : page.statusCode && page.statusCode >= 300 ? 'text-amber-500'
                          : 'text-emerald-500'
                      }`}>{page.statusCode ?? '\u2014'}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {page.isAsset ? <span className="text-xs text-[#8B8FA7]">Asset</span>
                        : page.isIndexable ? <span className="text-xs text-emerald-600">Oui</span>
                        : <span className="text-xs text-red-500">Non</span>}
                    </td>
                    <td className="px-4 py-3 text-[#1B2559] text-xs max-w-xs">
                      <span className="block truncate" title={page.currentValue}>{page.currentValue}</span>
                    </td>
                    <td className="px-4 py-3 text-[#555A73] text-xs max-w-sm">{page.suggestion}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
